import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { getStripeEnvironment } from "@/lib/stripe";

export type WorkspaceAddon = {
  id: string;
  stripe_subscription_id: string;
  status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
};

const ACTIVE_STATUSES = new Set(["active", "trialing", "past_due"]);

/**
 * Workspace add-on subscriptions for the current user. Cached via TanStack
 * Query so multiple consumers share one request and the result survives
 * route changes (no duplicate fetches on every navigation).
 */
export function useWorkspaceAddons() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const env = getStripeEnvironment();
  const qc = useQueryClient();
  const queryKey = useMemo(() => ["workspace_addons", userId, env] as const, [userId, env]);

  const { data: addons = [], isLoading, refetch } = useQuery({
    queryKey,
    enabled: !!userId,
    staleTime: 60_000,
    gcTime: 30 * 60_000,
    queryFn: async (): Promise<WorkspaceAddon[]> => {
      const { data, error } = await supabase
        .from("workspace_addons")
        .select("id, stripe_subscription_id, status, current_period_end, cancel_at_period_end")
        .eq("user_id", userId!)
        .eq("environment", env);
      if (error) throw error;
      return (data ?? []) as WorkspaceAddon[];
    },
  });

  useEffect(() => {
    if (!userId) return;
    const onFocus = () => qc.invalidateQueries({ queryKey });
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [userId, qc, queryKey]);

  const count = addons.filter((a) => {
    const stillInPeriod =
      !a.current_period_end || new Date(a.current_period_end) > new Date();
    return ACTIVE_STATUSES.has(a.status) && stillInPeriod;
  }).length;

  return {
    addons,
    count,
    loading: !!userId && isLoading,
    refresh: async () => { await refetch(); },
  };
}
